import { apiService } from '@/lib/api-functions/api-service';
import { UserProfile } from './storeTypes';



/**
 * Get user profile by ID (or current user if no ID provided)
 */
export async function getUserProfile(
  userId?: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<UserProfile> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Call the API to get the profile
    const result = await apiService.getProfile(userId);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch profile');
    }
    return result.data;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch profile';
    console.error("getUserProfile - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update user profile information
 */
export async function updateUserProfile(
  updates: {
    id?: string;
    full_name?: string;
    avatar_url?: string;
    settings?: Record<string, unknown>;
    bio?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<UserProfile> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Call the API to update the profile
    const result = await apiService.updateProfile(updates);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update profile');
    }

    return result.data;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update profile';
    console.error("updateUserProfile - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update user's full name specifically
 */
export async function updateUserFullName(
  fullName: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<UserProfile> {
  return updateUserProfile({ full_name: fullName }, toast);
}

/**
 * Update user's avatar URL specifically
 */
export async function updateUserAvatarUrl(
  avatarUrl: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<UserProfile> {
  return updateUserProfile({ avatar_url: avatarUrl }, toast);
}

/**
 * Update user's settings specifically
 * Note: The backend will merge these settings with existing settings,
 * so you only need to provide the fields you want to update
 */
export async function updateUserSettings(
  settings: Record<string, unknown>,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<UserProfile> {
  return updateUserProfile({ settings }, toast);
}
